'use client';

import React from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { FilterSelect, ResponsiveTooltip } from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  SearchInputIc,
  ResetFiltersIc,
  FilterSourceCircleIc,
  UserAccountFilterIc,
} from '../../../../components/icons';
import { getStatusFilterOptions, getSourceFilterOptions } from '../constants';
import { FiltersProps } from '../types';
import { Typography } from '../../../../components';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  isLoading,
  hasActiveFilters,
  statusCounts,
  onSearchChange,
  onFilterChange,
  onResetFilters,
}) => {
  const { t } = useTranslations();

  return (
    <div className="pb-6 flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4 md:flex-wrap md:justify-start max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
          <Input
            type="text"
            placeholder={t('partner.partnerAccounts.searchPlaceholder')}
            size="sm"
            value={localSearch}
            onChange={(e) => onSearchChange(e.target.value)}
            endContent={<SearchInputIc />}
            classNames={{
              mainWrapper: 'w-[17.0625rem]',
              base: 'w-[17.0625rem]',
              input: 'text-sm text-auth-ink placeholder:text-neutral-700',
              inputWrapper:
                'rounded-2 h-9 w-[17.0625rem] bg-auth-surface border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
              innerWrapper: 'bg-transparent',
            }}
          />
          <FilterSelect
            placeholder={t('partner.partnerAccounts.statusFilter') || 'Статус'}
            options={getStatusFilterOptions(t)}
            selectedKey={filters.status}
            variant="campaign"
            onSelectionChange={(value) => onFilterChange('status', value)}
            startContent={<UserAccountFilterIc />}
            autoWidth={true}
          />
          <FilterSelect
            placeholder={t('partner.partnerAccounts.sourceFilter') || 'Источник аккаунта'}
            options={getSourceFilterOptions(t)}
            selectedKey={filters.source}
            variant="campaign"
            onSelectionChange={(value) => onFilterChange('source', value === 'all' ? '' : value)}
            startContent={<FilterSourceCircleIc />}
            autoWidth={true}
          />
          {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
          {hasActiveFilters && (
            <Tooltip content={t('partner.revshare.offers.filters.resetTooltip')} placement="top">
              <Button
                //@ts-ignore
                size="xs"
                className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
                onPress={onResetFilters}
              >
                <ResetFiltersIc />
              </Button>
            </Tooltip>
          )}
        </div>
        {/* Info line with all status counts */}
        <div className="flex items-center gap-1 text-sm text-auth-muted flex-wrap">
          <Typography variant="body3" className="!text-auth-muted">
            {t('partner.partnerAccounts.total')}:
          </Typography>
          <Typography variant="label3">{statusCounts.total}</Typography>
          <span>(</span>
          <div className="flex items-center gap-1">
            <div className="size-[0.4375rem] rounded-full bg-success"></div>
            <Typography variant="body3" className="!text-auth-muted">
              {t('partner.partnerAccounts.statusActive')}:
            </Typography>
            <Typography variant="label3">{statusCounts.active},</Typography>
          </div>
          <div className="flex items-center gap-1">
            <div className="size-[0.4375rem] rounded-full bg-neutral-500"></div>
            <Typography variant="body3" className="!text-auth-muted">
              {t('partner.partnerAccounts.statusDeactivated')}:
            </Typography>
            <Typography variant="label3">{statusCounts.deactivated},</Typography>
          </div>
          <div className="flex items-center gap-1">
            <div className="size-[0.4375rem] rounded-full bg-accent"></div>
            <Typography variant="body3" className="!text-auth-muted">
              {t('partner.partnerAccounts.statusModeration')}:
            </Typography>
            <Typography variant="label3">{statusCounts.moderation}</Typography>
          </div>
          <span>)</span>
        </div>
      </div>
    </div>
  );
};

export default FiltersSection;
